import { Text, VSpacer } from '@/components/DesignSystem';
import { AccountGroupRole } from '@shared/enums';
import AccountBox from '@mui/icons-material/AccountBox';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Divider,
  Stack,
  useTheme,
} from '@mui/material';
import { blue, deepOrange, purple, teal } from '@mui/material/colors';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ApiUser } from '@api/interfaces';
import { useMediaQuery } from "@/hooks/useMediaQuery";

interface AboutAccountRolesModalContentProps {
  customerRole: AccountGroupRole,
  linkedAccounts: ApiUser[],
  pageType?: 'crd' | 'rp',
}

interface MixedGroupContent {
  description: string,
  sections: {
    heading: string,
    items: string[],
  }[];
}

export interface RoleContent {
  role: AccountGroupRole,
  title: string,
  description: string,
  iconColor: string,
  sections: {
    heading: string,
    items: string[],
  }[];
  mixedGroupContent?: MixedGroupContent;
}

export const getRoleDescription = (
  role: AccountGroupRole,
  isMixed: boolean,
  t: TFunction,
): string => {
  const roleContent = getRolesContent('crd', t).find((content) => content.role === role);
  if (!roleContent) return '';

  if (isMixed && roleContent.mixedGroupContent) {
    return roleContent.mixedGroupContent.description;
  }

  return roleContent.description;
};

const getMixedGroupContent = (
  description: string,
  page: 'rp' | 'crd',
  t: TFunction,
): MixedGroupContent => {
  return {
    description,
    sections: [
      {
        heading: page === 'crd'
          ? t(($) => $.aboutAccountRoles.headings.dashboard)
          : t(($) => $.aboutAccountRoles.headings.customerRecord),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.dashboardSharedStatus)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.dashboardSharedStatus),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.dashboardSecondaryAccess)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.dashboardSecondaryAccess),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.dashboardIndependentPartnerWallet)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.dashboardIndependentPartnerWallet),
        ],
      },
      {
        heading: t(($) => $.aboutAccountRoles.headings.wallet),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.walletSharedBalance)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.walletSharedBalance),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.walletEarnRedeem)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.walletEarnRedeem),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.walletIndependentPartner)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.walletIndependentPartner),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.pointsActivity,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.pointsActivitySharedOverview)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.pointsActivitySharedOverview),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.pointsActivityContribution)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.pointsActivityContribution),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.pointsActivityIndependentPartner)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.pointsActivityIndependentPartner),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.customerProgress,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.customerProgressSharedOverview)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.customerProgressSharedOverview),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.customerProgressCalculation)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.customerProgressCalculation),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.mixedGroup.crd.customerProgressIndependentPartner)
            : t(($) => $.aboutAccountRoles.mixedGroup.rp.customerProgressIndependentPartner),
        ],
      },
    ],
  };
};

export const getRolesContent = (page: 'rp' | 'crd', t: TFunction): RoleContent[] => [
  {
    role: AccountGroupRole.Primary,
    title: t(($) => $.aboutAccountRoles.roles.primary.title),
    description: t(
      ($) => $.aboutAccountRoles.roles.primary.description,
    ),
    iconColor: blue[800],
    sections: [
      {
        heading: page === 'crd'
          ? t(($) => $.aboutAccountRoles.headings.dashboard)
          : t(($) => $.aboutAccountRoles.headings.customerRecord),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.dashboardSharedStatus)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.dashboardSharedStatus),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.dashboardSecondaryAccess)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.dashboardSecondaryAccess),
        ],
      },
      {
        heading: t(($) => $.aboutAccountRoles.headings.wallet),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.walletSharedBalance)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.walletSharedBalance),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.walletRedemptions)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.walletRedemptions),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.pointsActivity,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.pointsActivitySharedOverview)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.pointsActivitySharedOverview),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.pointsActivityContribution)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.pointsActivityContribution),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.customerProgress,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.customerProgressSharedOverview)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.customerProgressSharedOverview),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.primary.crd.customerProgressCalculation)
            : t(($) => $.aboutAccountRoles.roles.primary.rp.customerProgressCalculation),
        ],
      },
    ],
    mixedGroupContent: getMixedGroupContent(
      t(($) => $.aboutAccountRoles.roles.primary.description),
      page,
      t,
    ),
  },
  {
    role: AccountGroupRole.Secondary,
    title: t(
      ($) => $.aboutAccountRoles.roles.secondary.title,
    ),
    description: t(
      ($) => $.aboutAccountRoles.roles.secondary.description,
    ),
    iconColor: teal[800],
    sections: [
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.customerRecord,
        ),
        items: [
          t(($) => $.aboutAccountRoles.roles.secondary.customerRecordContribution),
          t(($) => $.aboutAccountRoles.roles.secondary.customerRecordAccessRestriction),
        ],
      },
      {
        heading: t(($) => $.aboutAccountRoles.headings.wallet),
        items: [
          t(($) => $.aboutAccountRoles.roles.secondary.walletSharedBalance),
          t(($) => $.aboutAccountRoles.roles.secondary.walletRedemptions),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.pointsActivity,
        ),
        items: [
          t(($) => $.aboutAccountRoles.roles.secondary.pointsActivitySharedOverview),
          t(($) => $.aboutAccountRoles.roles.secondary.pointsActivityContribution),
        ],
      },
    ],
    mixedGroupContent: getMixedGroupContent(
      t(($) => $.aboutAccountRoles.roles.secondary.description),
      page,
      t,
    ),
  },
  {
    role: AccountGroupRole.Partner,
    title: t(($) => $.aboutAccountRoles.roles.partner.title),
    description: t(
      ($) => $.aboutAccountRoles.roles.partner.description,
    ),
    iconColor: purple[500],
    sections: [
      {
        heading: page === 'crd'
          ? t(($) => $.aboutAccountRoles.headings.dashboard)
          : t(($) => $.aboutAccountRoles.headings.customerRecord),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.dashboardSharedStatus)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.dashboardSharedStatus),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.dashboardEqualAccess)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.dashboardEqualAccess),
        ],
      },
      {
        heading: t(($) => $.aboutAccountRoles.headings.wallet),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.walletSharedBalance)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.walletSharedBalance),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.walletEarnRedeem)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.walletEarnRedeem),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.pointsActivity,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.pointsActivityAllAccounts)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.pointsActivityAllAccounts),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.pointsActivityContribution)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.pointsActivityContribution),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.customerProgress,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.customerProgressSharedOverview)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.customerProgressSharedOverview),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.partner.crd.customerProgressCalculation)
            : t(($) => $.aboutAccountRoles.roles.partner.rp.customerProgressCalculation),
        ],
      },
    ],
    mixedGroupContent: getMixedGroupContent(
      t(($) => $.aboutAccountRoles.roles.partner.description),
      page,
      t,
    ),
  },
  {
    role: AccountGroupRole.IndependentPartner,
    title: t(
      ($) => $.aboutAccountRoles.roles.independentPartner.title,
    ),
    description: t(
      ($) => (
        $.aboutAccountRoles.roles.independentPartner.description
      ),
    ),
    iconColor: deepOrange[900],
    sections: [
      {
        heading: page === 'crd'
          ? t(($) => $.aboutAccountRoles.headings.dashboard)
          : t(($) => $.aboutAccountRoles.headings.customerRecord),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.independentPartner.crd.dashboardPersonalWallet)
            : t(($) => $.aboutAccountRoles.roles.independentPartner.rp.dashboardPersonalWallet),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.independentPartner.crd.dashboardWalletAccess)
            : t(($) => $.aboutAccountRoles.roles.independentPartner.rp.dashboardWalletAccess),
        ],
      },
      {
        heading: t(($) => $.aboutAccountRoles.headings.wallet),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.independentPartner.crd.walletIndividualBalance)
            : t(($) => $.aboutAccountRoles.roles.independentPartner.rp.walletIndividualBalance),
          page === 'crd'
            ? t(($) =>
              $.aboutAccountRoles.roles.independentPartner.crd.walletOtherAccountsRestricted)
            : t(($) =>
              $.aboutAccountRoles.roles.independentPartner.rp.walletOtherAccountsRestricted),
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.independentPartner.crd.walletEarnRedeem)
            : t(($) => $.aboutAccountRoles.roles.independentPartner.rp.walletEarnRedeem),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.pointsActivity,
        ),
        items: [
          page === 'crd'
            ? t(($) =>
              $.aboutAccountRoles.roles.independentPartner.crd.pointsActivityPersonalOverview)
            : t(($) =>
              $.aboutAccountRoles.roles.independentPartner.rp.pointsActivityPersonalOverview),
          page === 'crd'
            ? t(($) =>
              $.aboutAccountRoles.roles.independentPartner.crd.pointsActivityOtherExcluded)
            : t(($) =>
              $.aboutAccountRoles.roles.independentPartner.rp.pointsActivityOtherExcluded),
        ],
      },
      {
        heading: t(
          ($) => $.aboutAccountRoles.headings.customerProgress,
        ),
        items: [
          page === 'crd'
            ? t(($) => $.aboutAccountRoles.roles.independentPartner.crd.customerProgressGroupStatus)
            : t(($) => $.aboutAccountRoles.roles.independentPartner.rp.customerProgressGroupStatus),
          ...(page === 'crd'
            ? [t(($) =>
              $.aboutAccountRoles.roles.independentPartner.crd.customerProgressPersonalSpending)]
            : []),
        ],
      },
    ],
    mixedGroupContent: getMixedGroupContent(
      t(
        ($) => (
          $.aboutAccountRoles.roles.independentPartner.description
        ),
      ),
      page,
      t,
    ),
  },
];

export const AboutAccountRolesModalContent = ({
  customerRole,
  linkedAccounts,
  pageType = 'crd',
}: AboutAccountRolesModalContentProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [selectedAccountRole, setSelectedAccountRole] = useState<
    AccountGroupRole | 'MIXED_GROUP' | null
  >(pageType === 'crd' ? customerRole : null);
  const { isMobile } = useMediaQuery();

  const { linkedAccountRoles, isMixedGroup } = useMemo(() => {
    if (pageType === 'rp') {
      return {
        linkedAccountRoles: Object.values(AccountGroupRole),
        isMixedGroup: true,
      };
    }
    const linkedAccountRoles = new Set<AccountGroupRole>([customerRole]);
    linkedAccounts.forEach((linked) => {
      if (linked.accountGroupRole) {
        linkedAccountRoles.add(linked.accountGroupRole as AccountGroupRole);
      }
    });

    const hasPartner = linkedAccountRoles.has(AccountGroupRole.Partner);
    const hasIndependentPartner = linkedAccountRoles.has(AccountGroupRole.IndependentPartner);
    const hasPrimaryAndSecondary = linkedAccountRoles.has(AccountGroupRole.Primary)
      && linkedAccountRoles.has(AccountGroupRole.Secondary);

    const accountTypeCount = [hasPrimaryAndSecondary, hasPartner, hasIndependentPartner]
      .filter(Boolean).length;

    const hasMixedRoles = accountTypeCount >= 2;

    return {
      linkedAccountRoles: [customerRole],
      isMixedGroup: hasMixedRoles,
    };
  }, [customerRole, linkedAccounts, pageType]);

  // Filter role content to only show roles that exist in linked accounts
  const visibleRolesContent = useMemo(() => {
    return getRolesContent(pageType, t)
      .filter((roleContent) => linkedAccountRoles.includes(roleContent.role as AccountGroupRole))
      .map((roleContent) => {
        if (isMixedGroup && roleContent.mixedGroupContent) {
          return {
            ...roleContent,
            description: roleContent.mixedGroupContent.description,
            sections: roleContent.mixedGroupContent.sections,
          };
        }
        return roleContent;
      });
  }, [linkedAccountRoles, isMixedGroup]);

  const handleChange = (role: AccountGroupRole | 'MIXED_GROUP') => (
    _event: React.SyntheticEvent,
    isExpanded: boolean,
  ) => {
    setSelectedAccountRole(isExpanded ? role : null);
  };

  return (
    <Stack>
      <Text
        category="title-large"
        sx={{ textAlign: isMobile ? 'center' : 'left' }}
      >
        {t($ => $.customerDashboard.howItWorks.buttons.aboutAccountRoles)}
      </Text>
      <VSpacer size="3" />
      <Text
        category="body-medium"
        sx={{ color: theme.palette.text.secondary, textAlign: isMobile ? 'center' : 'left' }}
      >
        {t(($) => $.aboutAccountRoles.subtitle)}
      </Text>
      <VSpacer size="5" />
      <Divider />
      <Stack divider={isMobile ? null : <Divider />} spacing={ isMobile ? 2 : 0 }>
        {visibleRolesContent.map((roleContent) => (
          <Accordion
            disableGutters
            elevation={0}
            expanded={selectedAccountRole === roleContent.role}
            key={roleContent.role}
            onChange={handleChange(roleContent.role)}
            sx={{
              border: isMobile ? `1px solid ${theme.palette.divider}` : 'none',
              borderRadius: isMobile ? '8px !important' : 'none',
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                borderLeft: `4px solid ${roleContent.iconColor}`,
                boxShadow: 'none',
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: '8px',
                boxShadow: 'none',
                padding: '10px 16px',
                '&.Mui-expanded': {
                  minHeight: '48px',
                  padding: '16px',
                  boxShadow: 'none',
                },
                '& .MuiAccordionSummary-content': {
                  margin: '12px 0',
                  '&.Mui-expanded': {
                    margin: '12px 0',
                  },
                },
                gap: '16px',
              }}
            >
              <Stack direction="row" spacing={2}>
                <Avatar
                  sx={{
                    bgcolor: theme.palette.common.white,
                    color: roleContent.iconColor,
                    height: 24,
                    width: 24,
                  }}
                >
                  <AccountBox />
                </Avatar>
                <Stack>
                  <Text category="title-medium" sx={{ color: theme.palette.text.primary }}>
                    {roleContent.title}
                  </Text>
                    <Text category="body-medium" sx={{ color: theme.palette.text.secondary }}>
                      {roleContent.description}
                    </Text>
                </Stack>
              </Stack>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: '10px 16px',
              }}
            >
              <Stack spacing={3}>
                {roleContent.sections.map((section, index) => (
                  <Box key={index}>
                    <Text
                      category="label-small"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                      }}
                    >
                      {section.heading}
                    </Text>
                    <VSpacer size="2" />
                    <Stack component="ul" spacing={1} sx={{ margin: 0, paddingLeft: '20px' }}>
                      {section.items.map((item, itemIndex) => (
                        <li key={itemIndex}>
                          <Text
                            category="body-medium"
                            sx={{ color: theme.palette.text.secondary }}
                          >
                            {item}
                          </Text>
                        </li>
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
      {!!visibleRolesContent.length && (
        <Divider />
      )}
    </Stack>
  );
};
